import { useMemo } from 'react';
import { ProcessingRuleItem, useQuery } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { Option, Pagination, PaginationParams } from '@/common/types';
import { API_URL, IProcessingRuleDto } from '@/dtos';
import { IProcessingTierDto } from '@/dtos/models/IProcessingTierDto';

import { useSortTiers as sortTiers } from '../../hooks';

export interface ProcessingRulePayload {
  id: 0;
  name: string;
  organization_type?: string;
  tiers: IProcessingTierDto[];
  isNew?: boolean;
  editable?: boolean;
}

export const QUERY_KEY = 'GET_PROCESSING_RULES';
export default (organizationPath?: string, isUsed?: boolean, params?: PaginationParams, enabled = true) => {
  const { data, isLoading, isFetching } = useQuery(
    [QUERY_KEY, organizationPath, params, isUsed],
    () =>
      api.get<Pagination<IProcessingRuleDto>>(API_URL.PROCESSING_RULES, {
        params: {
          organization_path: organizationPath,
          page: params?.page ? params.page - 1 : 0,
          ...(params?.searchString && { name: params.searchString }),
          ...(params?.perPage && { size: params.perPage }),
          ...(params?.sort && { sort: params.sort }),
          ...(isUsed !== undefined && { is_used: !!isUsed }),
        },
      }),
    {
      enabled: !!organizationPath && enabled,
    }
  );

  const total = useMemo(() => data?.data.totalElements || 0, [data]);
  const page = useMemo(() => (data?.data.number !== undefined ? data.data.number + 1 : 1), [data]);
  const totalPages = useMemo(() => data?.data.totalPages || 0, [data]);
  const formatedData = useMemo(() => {
    if (data?.data?.content === undefined) return undefined;

    return (data?.data?.content || []).map((item) => {
      const formatedItem: ProcessingRuleItem = {
        id: `${item.id}`,
        name: item.name || '',
        // @ts-ignore
        isNew: item.isNew,
        organizationType: item.organization_type,
        editable: item.editable,
        tiers: sortTiers(item.tiers || [])?.map((tier) => ({
          id: `${tier.id}`,
          name: tier.name || '',
          payoutDefinition: {
            id: `${tier.payout_definition?.id}`,
            name: tier.payout_definition?.name || '',
          },
          isSendTriggerNotification: tier.is_send_trigger_notification,
          triggers: tier.triggers,
          tierTriggers: (tier.tier_triggers || []).map((t) => ({
            id: _.toString(t.id),
            name: t.name,
            triggerCategoryType: t.trigger_category_type,
          })),
          trigger: tier.triggers?.[0],
          nextTierId: tier.next_tier_id ?? null,
          displayName: tier?.display_name,
          planId: `${tier?.plan_id || ''}`,
          planName: tier?.plan_name,
        })),
      };
      return formatedItem;
    });
  }, [data]);

  const formatedList = useMemo<Option[]>(() => {
    if (!data?.data?.content) return [];
    return data.data.content.map((item) => ({
      key: `${item.id}`,
      value: `${item.name}`,
      name: item.name,
    }));
  }, [data]);

  return {
    total,
    isLoading,
    isFetching,
    data: formatedData,
    list: formatedList,
    page,
    totalPages,
  };
};
